import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useSmartDeviceFlow from "../../../../hooks/useSmartDeviceFlow";
import { SMART_DEVICE_STEP_NAME_START } from "../../../../pages/smartDeviceInsurancePages/newSmartDevicePolicy/data";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { selectSmartDeviceForm, setSmartDeviceFormData } from "../../../../redux/features/smartDeviceFormSlice";
import BasicInfo from "../../../common/BasicInfo/BasicInfo";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import SmartDeviceJourneyLayout from "../../layouts/SmartDeviceJourneyLayout";
import DocumentsReadyInfo from "./documentsReadyInfo/documentsReadyInfo";

const SMART_DEVICE_BASIC_INFO = "SMART_DEVICE_BASIC_INFO";

const SmartDeviceStartPage = ({ onClose }) => {
  const dispatch = useDispatch();
  const { handleNextSubstep, currentSubstepIndex, currentStep, handlePrevSubstep } =
    useSmartDeviceFlow();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;
  const translate = useLanguageTranslate();

  // Get saved form data from Redux
  const smartDeviceFormData = useSelector(selectSmartDeviceForm);
  const savedBasicInfo = smartDeviceFormData?.[SMART_DEVICE_BASIC_INFO];

  // Convert Redux data to BasicInfo format
  const initialValues = useMemo(() => {
    if (!savedBasicInfo) return null;
    const birthDateParts = savedBasicInfo.birthDate?.split("-") || [];
    return {
      fullName: savedBasicInfo.fullName || "",
      email: savedBasicInfo.email || "",
      mobile: savedBasicInfo.contactNumber || "",
      year: birthDateParts[0] || "",
      month: birthDateParts[1] || "",
      day: birthDateParts[2] || "",
      fullDate: savedBasicInfo.birthDate || "",
    };
  }, [savedBasicInfo]);

  // If logged in, skip to documents ready step
  useEffect(() => {
    if (
      isLoggedIn &&
      currentStep?.name === SMART_DEVICE_STEP_NAME_START &&
      currentSubstepIndex !== 1
    ) {
      handleNextSubstep();
    }
  }, [isLoggedIn, currentStep, currentSubstepIndex, handleNextSubstep]);

  const handleBackToForm = () => {
    handlePrevSubstep();
  };

  const handleSaveBasicInfo = (basicInfo) => {
    // Save basic info to Redux
    dispatch(setSmartDeviceFormData({
      form_name: SMART_DEVICE_BASIC_INFO,
      data: basicInfo,
    }));
  };

  return (
    <div className="w-full h-full flex flex-col items-center p-5 pt-0 max767:p-0">
      {isLoggedIn ? (
        currentSubstepIndex === 1 ? (
          <DocumentsReadyInfo
            continueClickHandler={handleNextSubstep}
            hide_back_btn={true}
            show_close_btn={true}
            onClose={onClose}
            isFirstPage={true}
          />
        ) : (
          <div className="p-4">Loading...</div>
        )
      ) : (
        <>
          {currentSubstepIndex === 0 ? (
            <BasicInfo
              continueClickHandler={handleNextSubstep}
              onClose={onClose}
              title={translate(
                "New Smart Device Insurance",
                "تأمين الأجهزة الذكية"
              )}
              subtitle={translate(
                "Welcome! Let's start with some basic information.",
                "مرحبًا بك! لنبدأ ببعض المعلومات الأساسية."
              )}
              onSaveBasicInfo={handleSaveBasicInfo}
              JourneyLayoutComponent={SmartDeviceJourneyLayout}
              BottomStickyBtnLayoutComponent={BottomStickyBtnLayout}
              initialValues={initialValues}
              storageKey="smart_device_basic_info"
            />
          ) : currentSubstepIndex === 1 ? (
            <DocumentsReadyInfo
              continueClickHandler={handleNextSubstep}
              hide_back_btn={false}
              show_close_btn={false}
              onClose={onClose}
              customBackHandler={handleBackToForm}
              isFirstPage={false}
            />
          ) : (
            <div className="p-4">Loading...</div>
          )}
        </>
      )}
    </div>
  );
};

export default SmartDeviceStartPage;
